import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import FilerobotImageEditor, {
  TABS,
  TOOLS,
} from "react-filerobot-image-editor";
import useContent from "../../hooks/useContent";
import useLanguage from "../../hooks/useLanguage";

const TABS_IDS = [
  TABS.ADJUST,
  TABS.FINETUNE,
  TABS.FILTERS,
  TABS.WATERMARK,
  TABS.ANNOTATE,
  TABS.RESIZE,
];

const base64ToBlob = (base64) => {
  const [meta, data] = base64.split(",");
  const mimeMatch = meta.match(/data:(.*?);base64/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const binary = atob(data);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

export default function EditImageModal({ open, srcUrl, onClose, onSave }) {
  const { uploadStorehouse } = useContent();
  const [{ components: { mediaEditor } }] = useLanguage();
  const strings = mediaEditor.image;
  const filerobotTranslations = strings.filerobot;
  const muiTheme = useTheme();
  const [uploading, setUploading] = useState(false);
  const [objectUrl, setObjectUrl] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Map MUI palette to filerobot's underlying @scaleflex/ui token set.
  // Filerobot reads ~10 keys directly; its inner buttons (Save/Cancel/tool
  // selectors) are rendered with @scaleflex/ui components which use a much
  // larger palette (~50 tokens). We need to cover the full set so dark mode
  // looks pro — partial coverage leaves white buttons over dark panels.
  const filerobotTheme = useMemo(() => {
    const p = muiTheme.palette;
    const isDark = p.mode === "dark";

    const accent = p.primary?.main || "#1976d2";
    const accentHover = p.primary?.light || accent;
    const accentActive = p.primary?.dark || accent;
    const accentDisabled = isDark
      ? "rgba(255,255,255,0.12)"
      : "rgba(0,0,0,0.12)";

    const bgPrimary = p.background?.default || (isDark ? "#1e1e1e" : "#fff");
    const bgSecondary =
      p.background?.paper || (isDark ? "#262626" : "#f7f7f7");
    const bgHover = isDark
      ? "rgba(255,255,255,0.06)"
      : "rgba(0,0,0,0.04)";
    const bgActive = isDark
      ? "rgba(255,255,255,0.08)"
      : "rgba(0,0,0,0.04)";
    const bgPrimaryActive = isDark
      ? "rgba(255,255,255,0.12)"
      : "rgba(0,0,0,0.08)";

    const txtPrimary = p.text?.primary || (isDark ? "#e6e6e6" : "#1a1a1a");
    const txtSecondary =
      p.text?.secondary || (isDark ? "rgba(255,255,255,0.7)" : "#5f6b76");
    const txtPlaceholder = isDark
      ? "rgba(255,255,255,0.45)"
      : "rgba(0,0,0,0.45)";

    const iconsInvert = isDark ? "#1e1e1e" : "#ffffff";
    const iconsMuted = isDark
      ? "rgba(255,255,255,0.38)"
      : "rgba(0,0,0,0.38)";

    const bordersPrimary = isDark
      ? "rgba(255,255,255,0.18)"
      : "rgba(0,0,0,0.18)";
    const bordersSecondary = isDark
      ? "rgba(255,255,255,0.10)"
      : "rgba(0,0,0,0.12)";
    const bordersStrong = isDark
      ? "rgba(255,255,255,0.30)"
      : "rgba(0,0,0,0.30)";

    const error = p.error?.main || "#f44336";
    const success = p.success?.main || "#4caf50";
    const warning = p.warning?.main || "#ff9800";
    const info = p.info?.main || "#2196f3";

    return {
      palette: {
        // text
        "txt-primary": txtPrimary,
        "txt-secondary": txtSecondary,
        "txt-secondary-invert": iconsInvert,
        "txt-placeholder": txtPlaceholder,
        "txt-warning": warning,
        "txt-error": error,
        "txt-info": info,

        // accent
        "accent-primary": accent,
        "accent-primary-hover": accentHover,
        "accent-primary-active": accentActive,
        "accent-primary-disabled": accentDisabled,
        "accent-secondary-disabled": bgSecondary,
        "accent-stateless": accent,
        "accent-stateless_0_4_opacity": isDark
          ? "rgba(96,111,209,0.40)"
          : "rgba(104,121,235,0.40)",
        "accent_0_5_opacity": isDark
          ? "rgba(96,111,209,0.05)"
          : "rgba(104,121,235,0.05)",
        "accent_0_7_opacity": isDark
          ? "rgba(96,111,209,0.70)"
          : "rgba(96,111,209,0.70)",
        "accent_1_2_opacity": isDark
          ? "rgba(96,111,209,0.20)"
          : "rgba(104,121,235,0.12)",
        "accent_1_8_opacity": isDark
          ? "rgba(96,111,209,0.30)"
          : "rgba(104,121,235,0.18)",
        "accent_2_8_opacity": isDark
          ? "rgba(96,111,209,0.36)"
          : "rgba(104,121,235,0.28)",
        // legacy/typo alias filerobot reads in places
        "access-primary": accent,

        // background
        "bg-grey": isDark ? "rgba(255,255,255,0.08)" : "rgba(216,224,232,1)",
        "bg-stateless": bgSecondary,
        "bg-active": bgActive,
        "bg-base-light": isDark ? "rgba(96,111,209,0.10)" : "rgba(235,239,252,1)",
        "bg-base-medium": isDark ? "rgba(96,111,209,0.18)" : "rgba(227,231,251,1)",
        "bg-primary": bgPrimary,
        "bg-primary-light": bgSecondary,
        "bg-primary-hover": bgHover,
        "bg-primary-active": bgPrimaryActive,
        "bg-primary-stateless": bgSecondary,
        "bg-primary-0-5-opacity": isDark
          ? "rgba(0,0,0,0.5)"
          : "rgba(255,255,255,0.5)",
        "bg-secondary": bgSecondary,
        "bg-hover": bgHover,
        "bg-tooltip": isDark ? "rgba(50,50,50,0.95)" : "rgba(79,98,118,1)",

        // icons
        "icon-primary": txtPrimary,
        "icons-primary": txtPrimary,
        "icons-primary-opacity-0-6": isDark
          ? "rgba(255,255,255,0.60)"
          : "rgba(93,109,126,0.60)",
        "icons-secondary": txtSecondary,
        "icons-placeholder": txtPlaceholder,
        "icons-invert": iconsInvert,
        "icons-muted": iconsMuted,
        "icons-primary-hover": txtPrimary,
        "icons-secondary-hover": txtPrimary,

        // buttons
        "btn-primary-text": "#ffffff",
        "btn-primary-text-0-6": "rgba(255,255,255,0.6)",
        "btn-primary-text-0-4": "rgba(255,255,255,0.4)",
        "btn-secondary-text": txtPrimary,
        "btn-disabled-text": isDark
          ? "rgba(255,255,255,0.30)"
          : "rgba(0,0,0,0.30)",

        // links
        "link-primary": accent,
        "link-stateless": accent,
        "link-hover": accentHover,
        "link-active": accentActive,
        "link-muted": txtSecondary,
        "link-pressed": accent,

        // borders
        "borders-primary": bordersPrimary,
        "borders-primary-hover": txtSecondary,
        "borders-secondary": bordersSecondary,
        "borders-strong": bordersStrong,
        "borders-invert": isDark ? "#262626" : "#556373",
        "border-hover-bottom": isDark
          ? "rgba(96,111,209,0.30)"
          : "rgba(104,121,235,0.18)",
        "border-active-bottom": accentActive,
        "border-primary-stateless": bordersPrimary,
        "borders-disabled": accentDisabled,
        "borders-button": bordersPrimary,
        "borders-item": bordersSecondary,

        // states
        error: error,
        "error-hover": error,
        "error-active": error,
        "error-0-28-opacity": isDark
          ? "rgba(246,61,61,0.40)"
          : "rgba(185,33,46,0.28)",
        "error-0-12-opacity": isDark
          ? "rgba(246,61,61,0.20)"
          : "rgba(255,86,78,0.10)",
        success: success,
        "success-hover": success,
        "success-Active": success,
        warning: warning,
        "warning-hover": warning,
        "warning-active": warning,
        info: info,
        modified: accent,

        // active secondary
        "active-secondary": bgSecondary,
        "active-secondary-hover": isDark
          ? "rgba(96,111,209,0.18)"
          : "rgba(104,121,235,0.08)",

        // tag
        tag: txtSecondary,

        // shadows
        "light-shadow": isDark
          ? "rgba(0,0,0,0.6)"
          : "rgba(146,166,188,0.20)",
        "medium-shadow": isDark
          ? "rgba(0,0,0,0.7)"
          : "rgba(146,166,188,0.24)",
        "large-shadow": isDark
          ? "rgba(0,0,0,0.8)"
          : "rgba(146,166,188,0.32)",
        "x-large-shadow": isDark
          ? "rgba(0,0,0,0.9)"
          : "rgba(146,166,188,0.5)",
      },
      typography: {
        fontFamily: muiTheme.typography?.fontFamily || "inherit",
      },
    };
  }, [muiTheme]);

  useEffect(() => {
    if (!open || !srcUrl) {
      return undefined;
    }

    let cancelled = false;
    let createdObjectUrl = null;

    const loadImage = async () => {
      setLoadingImage(true);
      setLoadError(null);
      try {
        const baseUrl = process.env.REACT_APP_API_URL || "";
        const token = localStorage.getItem("x-token");
        const endpoint = `${baseUrl}/api/media/proxy?url=${encodeURIComponent(
          srcUrl
        )}`;
        const response = await fetch(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error(
            `Failed to load image (${response.status} ${response.statusText})`
          );
        }
        const blob = await response.blob();
        if (cancelled) return;
        createdObjectUrl = URL.createObjectURL(blob);
        setObjectUrl(createdObjectUrl);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err?.message || "Failed to load image");
      } finally {
        if (!cancelled) setLoadingImage(false);
      }
    };

    loadImage();

    return () => {
      cancelled = true;
      if (createdObjectUrl) URL.revokeObjectURL(createdObjectUrl);
      setObjectUrl(null);
    };
  }, [open, srcUrl]);

  const handleSave = useCallback(
    async (savedImageData) => {
      const { imageBase64, fullName, name, extension, mimeType } =
        savedImageData;
      if (!imageBase64) return;
      try {
        setUploading(true);
        const blob = base64ToBlob(imageBase64);
        const fileName = fullName || `${name || "edited"}.${extension || "png"}`;
        const file = new File([blob], fileName, {
          type: mimeType || blob.type,
        });
        const result = await uploadStorehouse([file]);
        const newUrl = Array.isArray(result) ? result[0]?.url : result?.url;
        if (newUrl) {
          onSave(newUrl);
          onClose();
        }
      } finally {
        setUploading(false);
      }
    },
    [uploadStorehouse, onSave, onClose]
  );

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: "90vh" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h3">{strings.title}</Typography>
        <IconButton aria-label={strings.close} onClick={onClose}>
          <CloseIcon fontSize="inherit" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ position: "relative", p: 0, height: "100%" }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            // @scaleflex/ui's Input/Textarea hardcodes the LIGHT palette for
            // its background and border (see input.utils.js — `lightPalette`
            // imported directly), so the theme prop can't reach those.
            // Override here so inputs/textareas/labels match the active mode.
            "& .SfxInput-root, & .SfxTextarea-root": {
              backgroundColor: `${muiTheme.palette.background.paper} !important`,
              borderColor: `${
                muiTheme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.18)"
                  : "rgba(0,0,0,0.18)"
              } !important`,
              color: `${muiTheme.palette.text.primary} !important`,
            },
            "& .SfxInput-Base, & .SfxTextarea-Base": {
              color: `${muiTheme.palette.text.primary} !important`,
              caretColor: muiTheme.palette.text.primary,
            },
            "& .SfxInput-Base::placeholder, & .SfxTextarea-Base::placeholder":
              {
                color: `${
                  muiTheme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.45)"
                    : "rgba(0,0,0,0.45)"
                } !important`,
              },
            "& .SfxLabel-root": {
              color: `${muiTheme.palette.text.secondary} !important`,
            },
            // Some menu/select dropdowns also use BackgroundStateless via the
            // hardcoded util chain — pin them to paper too.
            "& .SfxMenuItem-root, & .SfxMenu-root": {
              backgroundColor: `${muiTheme.palette.background.paper}`,
              color: muiTheme.palette.text.primary,
            },
            "& .SfxMenuItem-root:hover": {
              backgroundColor:
                muiTheme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.04)",
            },
            // Mobile webkit UA stylesheets inject `background-color: buttonface`
            // (white) on <button> elements, overriding the Filerobot theme tokens.
            // Reset appearance and pin the background explicitly so dark mode
            // looks correct on iOS/Android.
            "& [class*='SfxButton']": {
              WebkitAppearance: "none",
              appearance: "none",
              backgroundColor: `${muiTheme.palette.background.paper} !important`,
              color: `${muiTheme.palette.text.primary} !important`,
            },
            "& [class*='SfxButton'][class*='primary']": {
              backgroundColor: `${muiTheme.palette.primary.main} !important`,
              color: "#ffffff !important",
            },
          }}
        >
          {loadingImage && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {!loadingImage && loadError && (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{loadError}</Alert>
            </Box>
          )}
          {!loadingImage && !loadError && objectUrl && (
            <FilerobotImageEditor
              source={objectUrl}
              onSave={handleSave}
              onBeforeSave={() => false}
              onClose={onClose}
              theme={filerobotTheme}
              translations={filerobotTranslations}
              tabsIds={TABS_IDS}
              defaultTabId={TABS.ADJUST}
              defaultToolId={TOOLS.CROP}
              savingPixelRatio={1}
              previewPixelRatio={1}
              closeAfterSave={false}
            />
          )}
        </Box>
        {uploading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              zIndex: (theme) => theme.zIndex.modal + 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
